<template>
  <main class='manage-fiat-accounts-wrapper'>
    <vs-prompt
      @close="handleKycPopupClose"
      buttons-hidden
      title="Upload KYC documents"
      :active="shouldShowKycModal"
      class="fiat-kyc-popup">
      <h3>For <span>{{ selectedIndividualAccount.first_name }} {{ selectedIndividualAccount.last_name }}</span></h3>
      <FiatKycForm
        :accountId="selectedIndividualAccount.id"
        :confirmOrder="isOrderToConfirm"
        :shouldResetKycForm="shouldResetKycForm"
        @processFail="(error) => handleKycUploadError(error)"
        @processSuccess="handleKycUploadSuccess()"
        @resetKycForm="shouldResetKycForm = false">
      </FiatKycForm>
      <p v-if="kycError">
        {{ this.kycError }}
      </p>
      <div class="action-group" v-if="canOrderKyc">
        <button @click="handleKycPopupClose" class="btn cancel outline">Cancel</button>
        <button @click="handleKycPopupAccept" class="btn confirm">Confirm</button>
      </div>
    </vs-prompt>

    <vs-popup
      :active.sync="shouldShowAccountDetails"
      @popup-close="handleAccountDetailsClose"
      title='Account details'
      id="fiatAccountDetailsPopup">
      <div class="action-group">
        <button class="btn icon-btn confirm" v-if="shouldDisplayEditMode" @click="saveModifications()">Save</button>
        <button class="btn icon-btn cross" v-if="shouldDisplayEditMode" @click="cancelModification()">Cancel</button>
        <button class="btn btn-primary icon-btn edit" @click="handleEditClick(selectedAccount)" v-if="!shouldDisplayEditMode">Edit</button>
        <button class="btn outline close" @click="handleAccountDetailsClose">X</button>
      </div>
      <div class="banner error" v-if="updateErrors.length > 0">
        Errors were found in the update submission
        <ul>
          <li v-for="e in updateErrors" :key="e.code">{{ e.message }}</li>
        </ul>
        If errors persist, please contact our <a href="mailto:support@triple-a.io">support team</a>.
      </div>
      <FiatAccountDetails
        :account="selectedAccount"
        :isCompany="selectedAccount && selectedAccount.type === 'company'"
        :isLoading="isAccountDetailsLoading"
        :bankDetails="bankAccountDetails"
        :shouldDisplayKycUploadButton="canUploadKycDoc && selectedAccount && selectedAccount.verification_status !== 'pending' && selectedAccount.verification_status !== 'confirmed'"
        :isEditMode="shouldDisplayEditMode"
        :editedAccount="editedAccount"
        :editedBankDetails="editedBankAccountDetails"
        :canEditIdentity="isIdentityEditable(selectedAccount)"
        @kyc-click="handleKycClick(selectedAccount)"
        :countryList="countryList">
      </FiatAccountDetails>
    </vs-popup>

    <fiat-account-edit-popup
      :accountToEdit="selectedAccount"
      :bankDetailsToEdit="bankAccountDetails"
      :shouldShowEdit="shouldDisplayEditMode"
      :shouldDisplayKycUploadButton="canUploadKycDoc && selectedAccount && selectedAccount.verification_status !== 'pending' && selectedAccount.verification_status !== 'confirmed'" 
      />

    <h1 class="page-title">Manage Fiat accounts</h1>
    <p v-if="hasRightsToSeePage"><b>Manage your accounts</b> related to fiat payouts here. <b>Create payouts</b> via <router-link to="/create-fiat-payout">its dedicated page</router-link>.
      <br />
      Can't find what you want ? <router-link to="/create-fiat-account/sender">Create Sender account</router-link>, <router-link to="/create-fiat-account/beneficiary">Create a Beneficiary account</router-link>,
      or contact our <a href="mailto:support@triple-a.io">support team</a>.
    </p>
    <p class="banner icon error" v-else-if="!isMerchantConfigLoading">You dont have the rights to see this page. Please contact your admin for access. Should you have any questions, our <a href="mailto:support@triple-a.io">support team</a> is just a message away.</p>
    <form @submit.prevent="handleSearch()">
      <label>Search by name
        <input type="search" placeholder="Type in the name of the account" v-model="searchForm.name" />
      </label>
      <input type="submit" class="btn btn-primary" value="Search" />
    </form>
    <h2>Individual Accounts</h2>
    <p v-if="!hasRightsToSeePage">You dont have access to this data.</p>
    <section v-else-if="!isIndividualAccountListLoading && individualAccountList.length > 0">
      <div class="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Account Name</th>
              <th>KYC Status</th>
              <th>Country</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody
            is="TransitionGroup"
            name="fade">
            <tr v-for="acc of individualAccountList" :key="acc.id">
              <td><button class="btn link-style" @click="handleAccountSelection(acc)">{{ `${acc.first_name} ${acc.last_name}` }}</button></td>
              <td>
                <tag>{{ acc.verification_status }}</tag>
              </td>
              <td>{{ getCountryName(acc.country_code) }}</td>
              <td>
                <button
                  class="btn btn-primary icon-btn upload-doc"
                  v-if="canUploadKycDoc && acc.verification_status !== 'pending' && acc.verification_status !== 'confirmed'"
                  :disabled="!canUploadKycDoc || acc.verification_status === 'pending' || acc.verification_status === 'confirmed'" @click="handleKycClick(acc)">
                  Add KYC
                </button>
                <button class="btn btn-primary icon-btn edit" @click="handleEditClick(acc)"></button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p>Total individual accounts : {{ totalResultsPerso }} </p>
      <div class="action-group">
        <button class="btn btn-primary" @click="individualAccountTablePage--" :disabled="individualAccountTablePage < 2">Previous</button>
        <button class="btn btn-primary" @click="individualAccountTablePage++" :disabled="!hasNextPerso">Next page</button>
      </div>
    </section>
    <p v-else-if="!isIndividualAccountListLoading && individualAccountList.length < 1" class="emptyTable">
      No individual accounts found. To add one, <router-link to="/create-fiat-account/sender">Create Sender account</router-link>, <router-link to="/create-fiat-account/beneficiary">Create a Beneficiary account</router-link>,
      or contact our <a href="mailto:support@triple-a.io">support team</a>.
    </p>
    <!-- Loader -->
    <div class="vs-con-loading__container" id="fiat-payout-perso-list-loader" :enabled="isIndividualAccountListLoading"></div>

    <h2>Corporate Accounts</h2>
    <p v-if="!hasRightsToSeePage">You dont have access to this data.</p>
    <section v-else-if="!isCorporateAccountListLoading && corporateAccountList.length > 0">
      <div class="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Registered Name</th>
              <!-- <th>KYC Status</th> -->
              <th>Currency</th>
              <th>Email</th>
              <th>Country</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody
            is="TransitionGroup"
            name="fade">
            <tr v-for="acc of corporateAccountList" :key="acc.id">
              <td><button class="btn link-style" @click="handleAccountSelection(acc)">{{ `${acc.registered_name}` }}</button></td>
              <!-- <td><tag>{{ acc.verification_status }}</tag></td> -->
              <td>{{ acc.quota_currency }}</td>
              <td>{{ acc.email }}</td>
              <td>{{ getCountryName(acc.country_code) }}</td>
              <td>
                <button class="btn btn-primary icon-btn edit" @click="handleEditClick(acc)"></button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p>Total corporate accounts : {{ totalResultsCorp }} </p>
      <div class="action-group">
        <button class="btn btn-primary" @click="corporateAccountTablePage--" :disabled="corporateAccountTablePage < 2">Previous</button>
        <button class="btn btn-primary" @click="corporateAccountTablePage++" :disabled="!hasNextCorpo">Next page</button>
      </div>
    </section>
    <p class="emptyTable" v-else-if="corporateAccountList.length < 1">
      No individual accounts found. To add one, <router-link to="/create-fiat-account/sender">Create Sender account</router-link>, <router-link to="/create-fiat-account/beneficiary">Create a Beneficiary account</router-link>,
      or contact our <a href="mailto:support@triple-a.io">support team</a>.
    </p>
    <!-- Loader -->
    <div class="vs-con-loading__container" id="fiat-payout-corpo-list-loader" :enabled="isCorporateAccountListLoading"></div>

    <h2>KYC statuses</h2>
    <p>Here is the list of KYC verification statuses and their meaning. May you have any questions or enquiries, please contact our support team at <a href="mailto:support@triple-a.io">support@triple-a.io</a></p>
    <dl>
      <div>
        <dt>
          <tag>None</tag>
        </dt>
        <dd>No KYC needed</dd>
      </div>
      <div>
        <dt>
          <tag>Missing</tag>
        </dt>
        <dd>KYC is needed, and not uploaded. Please upload your KYC document.</dd>
      </div>
      <div>
        <dt>
          <tag>Pending</tag>
        </dt>
        <dd>Your KYC document is being processed, please wait.</dd>
      </div>
      <div>
        <dt>
          <tag>Failed</tag>
        </dt>
        <dd>KYC failed. Please upload a better proof, or contact our <a href="mailto:support@triple-a.io">support@triple-a.io</a> team.</dd>
      </div>
      <div>
        <dt>
          <tag>Confirmed</tag>
        </dt>
        <dd>KYC passed.</dd>
      </div>
    </dl>
  </main>
</template>
<script>
import Tag from '../../atoms/Tag.vue';
import { PAYOUT_ACCOUNT_TYPES } from '../../constants';
import FiatAccountDetails from '../../molecules/FiatAccountDetails.vue';
import FiatAccountEditPopup from '../../molecules/FiatAccountEditPopup.vue';
import FiatKycForm from '../../molecules/FiatKycForm.vue';
export default {
  components: { Tag, FiatKycForm, FiatAccountDetails, FiatAccountEditPopup },
  data() {
    return {
      merchantConfig: null,
      countryList: [],
      resPerPage: 10,
      totalResultsPerso: 0,
      totalResultsCorp: 0,

      // individual accounts
      individualAccountList: [],
      isIndividualAccountListLoading: false,
      selectedIndividualAccount: {
        first_name: '',
        last_name: '',
      },
      individualAccountTablePage: 1,

      // corporate accounts
      corporateAccountList: [],
      isCorporateAccountListLoading: false,
      selectedCorporateAccount: null,
      corporateAccountTablePage: 1,

      // KYC modal
      shouldShowKycModal: false,
      isOrderToConfirm: false,
      shouldResetKycForm: false,
      canOrderKyc: true,
      kycError: '',

      // account details
      shouldShowAccountDetails: false,
      selectedAccount: null,
      isAccountDetailsLoading: false,
      bankAccountDetails: null,

      // Account modifications
      shouldDisplayEditMode: false,
      ogAccountDetails: null,
      editedAccount: null,
      editedBankAccountDetails: null,
      updateErrors: [],

      // rights
      canUploadKycDoc: false,
      hasRightsToSeePage: false,

      // search
      searchForm: {
        name: '',
      },

      // // request token
      // apitoken : {
      //   access_token : '',
      //   expires_in : '',
      // },

      // flags
      isMerchantConfigLoading: false,
    };
  },
  methods: {
    initPage() {
      this.resetRights();
      this.corporateAccountTablePage = 1;
      this.individualAccountTablePage = 1;
      this.searchForm.name = '';
      this.updateErrors = [];
      this.getCountryList();
      this.getMerchantConfig();
      this.getIndividualAccountList();
      this.getCorporateAccountList();
    },

    handleSearch() {
      this.individualAccountTablePage = 1;
      this.corporateAccountTablePage = 1;
      this.getIndividualAccountList();
      this.getCorporateAccountList();
    },

    handleAccountSelection(account) {
      this.selectedAccount = account;
      this.shouldShowAccountDetails = true;
      this.isAccountDetailsLoading = true;
      this.getFiatApiToken().then((token) => {
        this.$store.dispatch('getBankAccountDetails', { token: token, bankAccountId: this.selectedAccount.id })
          .then((response) => {
            this.bankAccountDetails = response.data;
            this.editedBankAccountDetails = JSON.parse(JSON.stringify(response.data));
            this.isAccountDetailsLoading = false;
          })
          .catch(() => {
            this.isAccountDetailsLoading = false;
            this.bankAccountDetails = null;
            this.editedBankAccountDetails = null;
          });
      });
    },

    saveModifications() {
      const payload = this.buildModificationPayload();
      const isAccountModified = Object.keys(payload.account).length > 0;
      const bankPayload = this.buildBankAccPayload();
      const isBankModified = bankPayload && bankPayload.bankAccount && Object.keys(bankPayload.bankAccount).length > 0;
      // If no modif, do nothing
      if (!isAccountModified && !isBankModified) {
        this.shouldDisplayEditMode = false;
        return;
      }
      const functionName = this.editedAccount.type === PAYOUT_ACCOUNT_TYPES.INDIVIDUAL.value ? 'updateFiatIndividualAccount' : 'updateFiatCorporateAccount';
      this.getFiatApiToken().then((token) => {
        payload.token = token;
        this.$store.dispatch(functionName, payload).then((res) => {
          this.selectedAccount = res.data;
          if (this.selectedAccount.type === 'company') {
            this.getCorporateAccountList();
          } else {
            this.getIndividualAccountList();
          }
          // if destination account is updated, update the bank details
          if (bankPayload) {
            bankPayload.token = token;
            this.$store.dispatch('updateFiatDestinationAccount', bankPayload)
              .then((res) => {
                this.bankAccountDetails[0] = JSON.parse(JSON.stringify(res.data));
                this.editedBankAccountDetails[0] = JSON.parse(JSON.stringify(res.data));
                this.shouldDisplayEditMode = false;
              })
              .catch((error) => {
                this.editErrorFactory(error);
              });
          } else {
            this.shouldDisplayEditMode = false;
          }
        }).catch((error) => {
          this.editErrorFactory(error);
        });
      }).catch((error) => {
        this.editErrorFactory(error);
      });
    },

    cancelModification() {
      this.shouldDisplayEditMode = false;
      this.editedAccount = this.ogAccountDetails;
      this.ogAccountDetails = this.selectedAccount;
      this.updateErrors = [];
    },

    buildModificationPayload() {
      const payload = {
        account: {},
        accountId: this.editedAccount.id,
      };
      for (let i of Object.keys(this.editedAccount)) {
        if (this.editedAccount[i] && this.editedAccount[i] !== this.ogAccountDetails[i]) {
          payload.account[i] = this.editedAccount[i];
        }
      }
      return payload;
    },

    buildBankAccPayload() {
      if (this.editedBankAccountDetails.length < 1) {
        return {};
      }
      const payload = {
        bankAccountId: this.editedBankAccountDetails[0].id,
        bankAccount: {},
      };
      if (this.selectedAccount.type === 'company') {
        this.editedBankAccountDetails[0].account.account_name = this.editedAccount.registered_name;
      } else {
        this.editedBankAccountDetails[0].account.account_name = `${this.editedAccount.first_name} ${this.editedAccount.last_name}`;
      }

      for (let i of Object.keys(this.editedBankAccountDetails[0])) {
        if (i === 'account') {
          for (let j of Object.keys(this.editedBankAccountDetails[0][i])) {
            if (this.editedBankAccountDetails[0][i][j] && this.editedBankAccountDetails[0][i][j] !== this.bankAccountDetails[0][i][j]) {
              payload.bankAccount[i] = this.editedBankAccountDetails[0][i];
              break;
            }
          }
        } else if (this.editedBankAccountDetails[0][i] && this.editedBankAccountDetails[0][i] !== this.bankAccountDetails[0][i]) {
          payload.bankAccount[i] = this.editedBankAccountDetails[0][i];
        }
      }
      return payload;
    },

    checkRights() {
      this.resetRights();
      if (this.merchantConfig) {
        this.canUploadKycDoc = this.merchantConfig.kyc_reliance === 'none';
        // trust -> no need doc
        // none -> need input
      }
      this.hasRightsToSeePage = this.$store.getters.activeMerchantGetter.is_fiat_payout;
    },

    isIdentityEditable(acc) {
      if (this.merchantConfig && this.merchantConfig.kyc_reliance === 'trust'
        || (acc && (acc.verification_status !== 'confirmed' || acc.verification_status !== 'pending'))) {
        return true;
      }
      return false;
    },

    getIndividualAccountList() {
      const payload = {
        page: this.individualAccountTablePage,
        per_page: this.resPerPage,
      };
      if (this.searchForm.name) {
        payload.name = this.searchForm.name;
      }
      this.totalResultsPerso = 0;
      this.isIndividualAccountListLoading = true;
      this.getFiatApiToken().then((token) => {
        this.$store.dispatch('fetchPayoutIndividuals', { token: token, queries: payload })
          .then((response) => {
            this.individualAccountList = response.data;
            this.isIndividualAccountListLoading = false;
            this.totalResultsPerso = response.headers['x-total'];
          })
          .catch((error) => {
            this.isIndividualAccountListLoading = false;
            console.log(error);
          });
      });
    },

    getCorporateAccountList() {
      const payload = {
        page: this.corporateAccountTablePage,
        per_page: this.resPerPage,
      };
      if (this.searchForm.name) {
        payload.name = this.searchForm.name;
      }
      this.isCorporateAccountListLoading = true;
      this.totalResultsCorp = 0;
      this.getFiatApiToken().then((token) => {
        this.$store.dispatch('fetchPayoutCompanies', { token: token, queries: payload })
          .then((response) => {
            this.corporateAccountList = response.data;
            this.isCorporateAccountListLoading = false;
            this.totalResultsCorp = response.headers['x-total'];
          })
          .catch((error) => {
            this.isCorporateAccountListLoading = false;
            console.log(error);
          });
      });
    },

    getMerchantConfig() {
      this.isMerchantConfigLoading = true;
      this.resetRights();
      // fetch merchant config. Will always be updated if active merchant changes
      this.getFiatApiToken().then((token) => {
        this.$store.dispatch('getMerchantFiatConfig', token)
          .then((response) => {
            this.isMerchantConfigLoading = false;
            this.merchantConfig = response.data;
            this.$store.commit('UPDATE_MERCHANT_FIAT_PAYOUT_CONFIG', response.data);
            this.checkRights();
          })
          .catch((error) => {
            this.isMerchantConfigLoading = false;
            console.log(error);
          });
      });

    },

    // helpers
    async getFiatApiToken() {
      const merchantKey = this.$store.getters.activeMerchantKeyGetter;
      const response = await this.$store.dispatch('getMerchantPaymentRequestToken', merchantKey);
      this.requestToken = response.data.access_token;
      return response.data.access_token;
    },

    getCountryList() {
      this.getFiatApiToken().then(token => {
        this.$store.dispatch('getFiatCountryList', token).then(res => {
          this.countryList = res.data;
        }).catch(e => {
          console.log(e);
          return;
        });
      });
    },

    getCountryName(countryCode) {
      const country = this.countryList.find(c => c.code === countryCode);
      return country ? country.name : '';
    },

    formatError(err, code) {
      if (err) {
        const c = code ? code : err.status ? err.status : '';
        return {
          message: `Error ${c} : ${err.message}`,
          code: c,
        };
      }
      return {
        message: 'An error occured',
        code: 500,
      };
    },

    editErrorFactory(err) {
      this.updateErrors = [];
      const code = err.response.status;
      err.response.data.errors.forEach((e) => {
        this.updateErrors.push(this.formatError(e, code));
      });
    },

    // Handlers
    handleKycClick(account) {
      this.selectedIndividualAccount = account;
      this.shouldShowKycModal = true;
      this.kycError = '';
      this.canOrderKyc = true;
    },

    handleKycPopupClose() {
      // Only allow closing if not loading KYC
      if (!this.isOrderToConfirm) {
        this.shouldResetKycForm = true;
        this.shouldShowKycModal = false;
        this.kycError = '';
        this.canOrderKyc = true;
        // refresh indiv list only since only indiv can upload kyc
        this.getIndividualAccountList();
      }
    },

    handleKycPopupAccept() {
      this.isOrderToConfirm = true;
    },

    handleAccountDetailsClose() {
      this.shouldShowAccountDetails = false;
      this.shouldDisplayEditMode = false;
      this.selectedAccount = null;
      this.updateErrors = [];
    },

    handleKycUploadSuccess() {
      this.isOrderToConfirm = false;
      this.canOrderKyc = false;
      this.kycError = '';
    },

    handleKycUploadError(err) {
      this.isOrderToConfirm = false;
      this.canOrderKyc = true;
      if (err) {
        this.kycError = `Error ${err.status} : ${err.data.message}`;
      }
    },

    handleEditClick(acc) {
      this.shouldDisplayEditMode = true;
      this.updateErrors = [];
      if (!this.shouldShowAccountDetails) {
        this.handleAccountSelection(acc);
      }
      this.ogAccountDetails = acc;
      this.editedAccount = { ...acc };
      this.editedBankAccountDetails = this.bankAccountDetails ? JSON.parse(JSON.stringify(this.bankAccountDetails)) : []; // cloning
    },

    // resets
    resetRights() {
      this.canUploadKycDoc = false;
      this.hasRightsToSeePage = false;
    },
  },
  mounted() {
    this.initPage();
  },
  computed: {
    currentActiveMerchant() {
      return this.$store.getters.activeMerchantGetter;
    },
    hasNextCorpo() {
      return (!this.isOrderToConfirm
        && this.resPerPage * this.corporateAccountTablePage < this.totalResultsCorp
      );
    },
    hasNextPerso() {
      return (!this.isOrderToConfirm
        && this.resPerPage * this.individualAccountTablePage < this.totalResultsPerso
      );
    },
  },
  watch: {
    currentActiveMerchant() {
      this.initPage();
    },
    isIndividualAccountListLoading(newVal) {
      const ID = '#fiat-payout-perso-list-loader';
      if (newVal) {
        this.$vs.loading({
          container: ID,
          scale: 1,
          background: '#f8f8f8',
          text: 'Fetching individual accounts ...',
        });
      } else {
        try {
          this.$vs.loading.close(ID + ' > .con-vs-loading');
        } catch (e) {
          console.log(e);
        }
      }
    },
    isCorporateAccountListLoading(newVal) {
      const ID = '#fiat-payout-corpo-list-loader';
      if (newVal) {
        this.$vs.loading({
          container: ID,
          scale: 1,
          background: '#f8f8f8',
          text: 'Fetching corporate accounts ...',
        });
      } else {
        this.$vs.loading.close(ID + ' > .con-vs-loading');
      }
    },
    corporateAccountTablePage(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.getCorporateAccountList();
        newVal - oldVal > 1 ? this.corporateAccountTablePage = oldVal : null;
      }
    },
    individualAccountTablePage(newVal, oldVal) {
      if (newVal !== oldVal) {
        newVal - oldVal > 1 ? this.individualAccountTablePage = oldVal : null;
        this.getIndividualAccountList();
      }
    },
    shouldShowAccountDetails(newVal) {
      if (newVal === false) {
        this.handleAccountDetailsClose();
      }
    },
  }
};
</script>
<style lang="scss">
.manage-fiat-accounts-wrapper {
  max-width: 1050px;

  h2 {
    margin-bottom: $margin-m;
    margin-top: $margin-l;
    color: $color-text;
  }

  form {
    margin-top: $margin-m;
    display: flex;
    align-items: flex-end;
    width: 100%;
    max-width: 650px;

    label {
      display: flex;
      flex-direction: column;
      flex-grow: 1;

      input {
        display: block;
        width: 100%;
        margin-top: $margin-s;
        height: 38px;
        border-radius: 5px;
        padding: 3px 12px;
        border: 0.5px solid lightgrey;
        font-size: large;
      }
    }

    .btn {
      flex-grow: 0;
      margin-left: $margin-s;
      height: 38px;
      width: 100px;
    }
  }

  div.table-wrapper {
    overflow-x: auto;
    padding: 0;
    margin: 0;
    box-shadow: 0 0 5px 0 #b5b5b5;
    border-radius: 8px;
    max-height: 500px;
    background: white;

    table {
      position: relative;
      z-index: 0;
      background: white;
      border-radius: 6px;
      margin: 0;
      border-collapse: collapse;
      border-spacing: 0;
      width: 100%;
      border: 1px solid #ddd;

      * {
        text-align: center;
      }

      thead {
        tr {
          position: sticky;
          top: -1px;
          background: white;
          z-index: 1;

          // box-shadow: inset 0px -1px 0px 0px #b5b5b5;
          th {
            font-size: 14px;
            padding: 12px;
            box-shadow: inset 0px -1px 0px 0px #b5b5b5;

            &:first-of-type {
              position: sticky;
              left: -1px;
              top: -1px;
              background: white;
              box-shadow: inset -0.5px -1px 0px 0px #b5b5b5;
            }

            &.one-line {
              white-space: nowrap;
            }
          }
        }
      }

      tbody {
        z-index: 0;

        tr {
          padding: 4px 6px;
          border-bottom: solid 1px #b5b5b5;
          transition: ease-in-out 0.2s;

          &:hover {
            background-color: #e1e1e1;
          }

          td {
            min-width: 100px;
            padding: 12px;
            white-space: nowrap;

            button.link-style {
              color: $color-text;
              background: transparent;
            }

            &:first-of-type {
              position: sticky;
              left: -1px;
              box-shadow: inset -0.5px 0px 0px 0px #b5b5b5;
            }

            &:last-of-type {
              display: flex;
              align-items: center;
              justify-content: center;

              button {
                position: relative;
                border-radius: 6px;
                border: none;
                text-align: center;
                font-size: 1rem;
                margin-right: 6px;

                &.link-style {
                  color: $color-text;
                  background: transparent;
                }

                &.icon-btn {
                  min-height: 32px;

                  svg {
                    float: left;
                  }

                  &.edit {
                    padding-left: 24px;

                    &:before {
                      width: 16px;
                      height: 16px;
                    }
                  }

                  &.upload-doc {
                    &:before {
                      top: 4px;
                    }
                  }
                }

                &:last-child {
                  margin-right: 0;
                }
              }
            }
          }
        }
      }

      .dates {
        white-space: nowrap;
      }

      .actions {
        display: flex;
        justify-content: center;
      }
    }
  }

  p {
    width: 100%;
    max-width: 850px;
  }

  dl {
    margin-top: $margin-m;

    div {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      margin-bottom: $margin-m;

      dt {
        margin-right: $margin-s;
      }
    }
  }

  section {
    p {
      margin-bottom: 0;
    }

    .action-group {
      display: flex;
      justify-content: center;
      margin-top: $margin-m;

      button {
        width: 150px;
        height: 40px;

        &.cancel {
          margin-right: $margin-s;
        }
      }
    }
  }
}

.fiat-kyc-popup {
  h3 {
    margin-top: $margin-s;
    margin-bottom: $margin-m;

    span {
      font-weight: bold;
    }
  }

  .action-group {
    display: flex;
    justify-content: space-between;
    margin-top: $margin-l;

    button {
      width: 150px;
      height: 40px;

      &.cancel {
        margin-right: $margin-s;
      }
    }
  }
}

#fiatAccountDetailsPopup {
  justify-content: end;

  .vs-popup {
    position: fixed;
    top: 0;
    bottom: 0;
    right: 0;
    min-width: auto;
    padding: 0;
    margin: 0;
    max-width: 800px;
    width: 600px;
    display: block;
    max-height: initial;
    height: 100vh;
    border-bottom-right-radius: 0;
    border-top-right-radius: 0;

    &>header.vs-popup--header {
      display: none;
    }

    div.action-group {
      display: flex;
      justify-content: flex-end;
      position: fixed;
      width: calc(600px - 5px - 12px);
      margin-top: $margin-s;
      z-index: 1;

      button.close {
        font-size: 1rem;
        width: 32px;
        height: 32px;
        margin-top: 0px;
        margin-right: $margin-s;
      }

      button.btn.icon-btn {
        padding-left: 28px;
        max-height: 32px;
        min-height: 32px;

        &::before {
          height: 16px;
          width: 16px;
        }

        &.confirm {
          background-color: $color-success;

          &:hover {
            background-color: $color-success-darker;
          }
        }
      }
    }

    div.banner {
      margin-top: 48px;
      margin-bottom: 0px;
    }

  }
}

#fiat-payout-perso-list-loader,
#fiat-payout-corpo-list-loader {
  width: 100%;
  z-index: 1;

  &[enabled] {
    height: 150px;
  }
}
</style>